import {
  ConsultantBookingContractListItemDataFragment,
  useGetConsultantContractsPersonalDataQuery,
  useSendConsultantBookingContractEmailToBAMutation,
  useSendConsultantContractEmailToConsultantMutation
} from "@bonliva-crm/graphql";
import {
  EmailEditorFormValues,
  emailEditorFormSchema
} from "@consultants/consultant-profile-new/email-editor/form-values";
import { useForm } from "react-hook-form";
import { Button, Color, Modal, SendIcon, useSnackbarsPipeline } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import { yupResolver } from "@hookform/resolvers/yup";
import { EmailEditor } from "@consultants/consultant-profile-new/email-editor/email-editor";
import { ContractUnion } from "../types";
import { useEffect } from "react";

type Props = {
  consultantId: string;
  contracts: ContractUnion[];
  contract: ContractUnion;
  customerContract?: boolean;
  open: boolean;
  setOpen: (o: boolean) => void;
};

export const ConsultantEmailEditor = (props: Props) => {
  const [sendConsultantContractEmailToConsultantMutation] =
    useSendConsultantContractEmailToConsultantMutation();
  const [sendConsultantBookingContractEmailToBA] =
    useSendConsultantBookingContractEmailToBAMutation();
  const { showError, showSuccess } = useSnackbarsPipeline();

  const childContracts = props.contracts?.reduce((a, c) => {
    if ("ChildContracts" in c) {
      return c.ChildContracts?.concat(a);
    }
    return a;
  }, []);

  const attachment = props.customerContract
    ? props.contract.Attachment.FileRef
    : childContracts.find((x) => x?._id === props.contract._id)?.Attachment.FileRef;

  const emailEditorForm = useForm<EmailEditorFormValues>({
    defaultValues: {
      recipients: [],
      subject: null,
      body: null,
      attachments: [attachment]
    },
    resolver: yupResolver(emailEditorFormSchema)
  });

  useEffect(() => {
    if (props.customerContract) {
      const recipients = props.customerContract
        ? (
            props.contract as ConsultantBookingContractListItemDataFragment
          )?.Recipients?.map((x) => ({ email: x, name: "" }))
        : [];
      emailEditorForm.setValue("recipients", recipients);
    }
  }, [props.customerContract]);

  useGetConsultantContractsPersonalDataQuery({
    variables: {
      id: props.consultantId
    },
    onCompleted: (data) => {
      const userInfo = data?.consultantById;
      if (!props.customerContract) {
        emailEditorForm.setValue("recipients", [
          {
            email: userInfo?.Email,
            name: userInfo?.Name
          }
        ]);
      }
    },
    onError: (e) => {
      showError(locale.ErrorOccurred);
      console.error(e);
    },
    fetchPolicy: "cache-first"
  });

  const { watch } = emailEditorForm;
  const recipients = watch("recipients");

  return (
    <Modal
      open={props.open}
      setOpen={props.setOpen}
      onClose={() => props.setOpen(false)}
      dimensions={{
        width: "100%",
        maxWidth: "60vw"
      }}
      modalContent={
        <EmailEditor
          form={emailEditorForm}
          disabledFields={{ recipients: recipients.length > 0 }}
        />
      }
      label={locale.SendMail}
      saveButtonDisabled={!emailEditorForm.formState.isValid}
      rightCta={
        <Button
          variant="contained"
          color="primary"
          disabled={!emailEditorForm.formState.isValid}
          onClick={() => {
            emailEditorForm.handleSubmit((data) => {
              if (props.customerContract) {
                sendConsultantBookingContractEmailToBA({
                  variables: {
                    body: data.body,
                    subject: data.subject,
                    recipients: data.recipients,
                    attachment: data.attachments?.map((x) => x.NodeID)
                  },
                  onCompleted: () => {
                    showSuccess(locale["Email sent successfully"]);
                  },
                  onError: (e) => {
                    showError(locale.ErrorOccurred);
                    console.error(e);
                  },
                  refetchQueries: ["GetConsultantBookingContracts"]
                });
                emailEditorForm.reset();
                props.setOpen(false);
                return;
              }
              sendConsultantContractEmailToConsultantMutation({
                variables: {
                  body: data.body,
                  subject: data.subject,
                  consultantId: props.consultantId,
                  attachment: data.attachments?.map((x) => x.NodeID)
                },
                onCompleted: () => {
                  showSuccess(locale["Email sent successfully"]);
                },
                onError: (e) => {
                  showError(locale.ErrorOccurred);
                  console.error(e);
                },
                refetchQueries: ["GetConsultantContracts"]
              });
            })();
            emailEditorForm.reset();
            props.setOpen(false);
          }}
          endIcon={
            <SendIcon
              htmlColor={Color.White}
              style={{ width: "0.75rem", height: "0.75rem" }}
            />
          }
        >
          {locale.Send}
        </Button>
      }
      disableEnforceFocus
    />
  );
};
